import { useEffect, useState } from 'react';

/**
 * Countries (ISO 3166-1 alpha-2, uppercase) that should NOT be able to view the site.
 */
export const BLOCKED_COUNTRIES: string[] = ['PH'];

/**
 * Specific IP addresses that are always allowed through, even from a blocked country.
 * (Fixed list — if a home IP changes, re-run the lookup and update this list.)
 */
export const ALLOWED_IPS: string[] = ['112.208.65.197', '192.82.52.74', '61.245.120.4'];

/**
 * Private unlock key. Opening the site with `?lpp_key=<BYPASS_KEY>` permanently trusts
 * that browser/device, so the owner can keep browsing from a blocked region.
 */
export const BYPASS_KEY = 'lpp-a7f3k9q2x8m4';

const BYPASS_QUERY_PARAM = 'lpp_key';
const TRUSTED_STORAGE_KEY = 'lpp_trusted';
const CACHE_KEY = 'lpp_geo_country';
const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours

export type GeoStatus = 'checking' | 'allowed' | 'blocked';

export type GeoReason = 'trusted-device' | 'allowed-ip' | 'country-ok' | 'blocked-country' | 'lookup-failed' | 'pending';

export interface GeoRestrictionResult {
  status: GeoStatus;
  reason: GeoReason;
  ip: string | null;
  country: string | null;
}

interface LookupResult {
  ip: string | null;
  country: string | null;
}

interface CachedResult extends LookupResult {
  at: number;
}

interface Decision {
  status: GeoStatus;
  reason: GeoReason;
}

function decide({ ip, country }: LookupResult): Decision {
  if (ip && ALLOWED_IPS.includes(ip)) return { status: 'allowed', reason: 'allowed-ip' };
  if (country && BLOCKED_COUNTRIES.includes(country)) return { status: 'blocked', reason: 'blocked-country' };
  if (!ip && !country) return { status: 'allowed', reason: 'lookup-failed' };
  return { status: 'allowed', reason: 'country-ok' };
}

function readCache(): LookupResult | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedResult;
    if (!parsed?.at || Date.now() - parsed.at > CACHE_TTL) return null;
    return { ip: parsed.ip ?? null, country: parsed.country ?? null };
  } catch {
    return null;
  }
}

function writeCache(result: LookupResult): void {
  try {
    const payload: CachedResult = { ...result, at: Date.now() };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable — ignore */
  }
}

function isDeviceTrusted(): boolean {
  try {
    if (localStorage.getItem(TRUSTED_STORAGE_KEY) === '1') return true;
  } catch {
    /* ignore */
  }
  try {
    const params = new URLSearchParams(window.location.search);
    const key = params.get(BYPASS_QUERY_PARAM);
    if (key && key === BYPASS_KEY) {
      try {
        localStorage.setItem(TRUSTED_STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function stripBypassParam(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(BYPASS_QUERY_PARAM)) {
      url.searchParams.delete(BYPASS_QUERY_PARAM);
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  } catch {
    /* ignore */
  }
}

async function lookup(signal: AbortSignal): Promise<LookupResult> {
  const endpoints = ['https://ipwho.is/', 'https://ipapi.co/json/'];
  for (const url of endpoints) {
    try {
      const res = await fetch(url, { cache: 'no-store', signal });
      if (!res.ok) continue;
      const data = await res.json();
      const country = data?.country_code ?? data?.countryCode ?? null;
      const ip = data?.ip ?? null;
      if (country || ip) {
        return {
          ip: ip ? String(ip) : null,
          country: country ? String(country).toUpperCase() : null,
        };
      }
    } catch {
      /* try the next endpoint */
    }
  }
  return { ip: null, country: null };
}

/**
 * Resolves whether the current visitor is allowed to view the site based on
 * their approximate country (derived from their IP address).
 *
 * Allow-through order:
 *   1. Trusted device (bypass link opened, or previously trusted)
 *   2. IP address present in ALLOWED_IPS
 *   3. Country not in BLOCKED_COUNTRIES
 *
 * Fails OPEN: if the lookup cannot be completed, the visitor is allowed through
 * so that a third-party outage never locks out everyone worldwide.
 */
export function useGeoRestriction(): GeoRestrictionResult {
  const [result, setResult] = useState<GeoRestrictionResult>({
    status: 'checking',
    reason: 'pending',
    ip: null,
    country: null,
  });

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 6000);

    const cleanup = () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };

    // 1) Trusted device always wins.
    if (isDeviceTrusted()) {
      stripBypassParam();
      setResult({ status: 'allowed', reason: 'trusted-device', ip: null, country: null });
      return cleanup;
    }
    stripBypassParam();

    // 2) Reuse a recent lookup when available.
    const cached = readCache();
    if (cached) {
      const decision = decide(cached);
      setResult({ status: decision.status, reason: decision.reason, ip: cached.ip, country: cached.country });
      return cleanup;
    }

    // 3) Fresh lookup.
    (async () => {
      const lookupResult = await lookup(controller.signal);
      if (!active) return;
      if (lookupResult.ip || lookupResult.country) writeCache(lookupResult);
      const decision = decide(lookupResult);
      setResult({
        status: decision.status,
        reason: decision.reason,
        ip: lookupResult.ip,
        country: lookupResult.country,
      });
    })();

    return cleanup;
  }, []);

  return result;
}